/**
 * Flechas y Enter en todos los buscadores: con el cursor en un campo de búsqueda, ↑ ↓ recorren la lista
 * de resultados que está debajo (role="listbox", data-kbnav o una tabla) y Enter elige la marcada.
 * Los buscadores que ya manejan sus teclas (punto de venta, elegir cliente…) no se tocan: lo que ellos
 * resuelven queda resuelto (preventDefault) y esto no hace nada.
 */
const LIST_SEL = '[role="listbox"], [data-kbnav], .picker-results, .search-results, table.table > tbody';

function findList(input: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = input.parentElement;
  for (let depth = 0; el && depth < 8; depth++, el = el.parentElement) {
    for (const c of Array.from(el.querySelectorAll<HTMLElement>(LIST_SEL))) {
      if (c.contains(input) || c.closest("[data-kbnav-off]")) continue;
      // Que esté después del campo y se vea.
      if (!(input.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (!c.offsetParent && getComputedStyle(c).position !== "fixed") continue;
      return c;
    }
  }
  return null;
}

const PICK_SEL = '.picker-results, .tpanel-results, [role="listbox"]:not(.combo-list)';
/** El buscador que maneja una lista de resultados: el último campo de texto antes de la lista. */
function searchInputFor(list: HTMLElement): HTMLInputElement | null {
  let el: HTMLElement | null = list.parentElement;
  for (let depth = 0; el && depth < 4; depth++, el = el.parentElement) {
    const before = Array.from(el.querySelectorAll<HTMLInputElement>("input")).filter((i) =>
      ["text", "search", ""].includes(i.type) && !!(i.compareDocumentPosition(list) & Node.DOCUMENT_POSITION_FOLLOWING));
    if (before.length) return before[before.length - 1]!;
  }
  return null;
}

function options(list: HTMLElement): HTMLElement[] {
  if (list.tagName === "TBODY") return Array.from(list.children).filter((r): r is HTMLElement => r instanceof HTMLElement && getComputedStyle(r).cursor === "pointer");
  const direct = Array.from(list.children).filter((c): c is HTMLElement => c instanceof HTMLElement && c.matches('[role="option"], button, a[href], [data-kbnav-item]'));
  const all = direct.length ? direct : Array.from(list.querySelectorAll<HTMLElement>('[role="option"], [data-kbnav-item]'));
  return all.filter((o) => !(o as HTMLButtonElement).disabled && o.getAttribute("aria-disabled") !== "true");
}

function setActive(list: HTMLElement, opts: HTMLElement[], i: number) {
  for (const o of opts) o.classList.remove("kb-active");
  const o = opts[i];
  if (!o) { delete list.dataset.kbActive; return; }
  list.dataset.kbActive = String(i);
  o.classList.add("kb-active");
  o.scrollIntoView({ block: "nearest" });
}

let installed = false;
export function installKeyboardLists() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") return;
    const t = e.target as HTMLElement | null;
    if (!t || t.tagName !== "INPUT") return;
    const type = (t as HTMLInputElement).type;
    if (type && !["text", "search", "tel", "email", ""].includes(type)) return;
    const list = findList(t);
    if (!list) return;
    const opts = options(list);
    if (!opts.length) return;
    const cur = list.dataset.kbActive !== undefined ? Number(list.dataset.kbActive) : -1;
    if (e.key === "Enter") {
      const i = cur >= 0 && opts[cur]?.classList.contains("kb-active") ? cur : list.hasAttribute("data-enter-first") ? 0 : -1;
      if (i < 0) return;
      e.preventDefault();
      opts[i]!.click();
      delete list.dataset.kbActive;
      return;
    }
    e.preventDefault();
    const next = e.key === "ArrowDown" ? Math.min(opts.length - 1, cur + 1) : Math.max(0, cur - 1);
    setActive(list, opts, next);
  });
  // 1.71.0: al elegir un resultado de un buscador, el cursor vuelve a ese buscador (que queda en blanco) para seguir.
  document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest?.<HTMLElement>("button, [role=option]");
    const list = btn?.closest<HTMLElement>(PICK_SEL);
    if (!btn || !list) return;
    const input = searchInputFor(list);
    if (input) requestAnimationFrame(() => { 
      // Solo si el foco quedó en el botón elegido o se perdió (no si la elección abrió otra cosa).
      const a = document.activeElement;
      if (input.isConnected && !input.disabled && (!a || a === document.body || a === btn || !a.isConnected || list.contains(a))) input.focus();
    });
  });
  // Al escribir de nuevo se vuelve a empezar desde el primero.
  document.addEventListener("input", (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || t.tagName !== "INPUT") return;
    const list = findList(t);
    if (list) { for (const o of Array.from(list.querySelectorAll(".kb-active"))) o.classList.remove("kb-active"); delete list.dataset.kbActive; }
  });
}
